/* eslint-disable @typescript-eslint/no-shadow */
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { pickScore } from '@upright/common/model/output/scores';
import { ImpactScoresByIVV } from '@upright/common/model/output/scores/types';
import { Valence, ValueChain } from '@upright/common/model/types';
import { Bounds, CarouselLayout, Side } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { sideToValence, toBounds } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/utils';
import { ImpactInsight } from '@upright/ui/common/upright/core/impact-lenses/insights/common/types';
import { DimensionBarsLayout } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/dimension-bars-layout';

export interface ImpactBarsLayout {
  /**
   * Area available for bar
   */
  impactBarRangeArea: (side: Side, impact: ImpactCategoryId) => Bounds;

  /**
   * Area that bar will take
   */
  impactBarArea: (side: Side, impact: ImpactCategoryId, valueChain?: ValueChain) => Bounds;

  /**
   * Area reserved for bar label
   */
  impactBarLabelArea: (side: Side, impact: ImpactCategoryId) => Bounds;

  /**
   * Area taken by bar and label
   */
  impactBarAndLabelArea: (side: Side, impact: ImpactCategoryId) => Bounds;

  /**
   * Area of the ghost-baseline tail appended after the bar (see
   * `NetImpactLensData.ghostScores`); undefined when there is no ghost data
   * or the tail would be sub-pixel.
   */
  impactGhostTailArea: (side: Side, impact: ImpactCategoryId) => Bounds | undefined;

  /**
   * TODO: remove
   */
  impactBarDividerArea: (index: number, side: Side, impact: ImpactCategoryId) => Bounds;

  dimensionLayout: DimensionBarsLayout | undefined;
}

export interface Data {
  scores?: ImpactScoresByIVV;
  insights?: ImpactInsight[];
  /** Ghost-baseline scores — see `NetImpactLensData.ghostScores`. */
  ghostScores?: ImpactScoresByIVV;
}

export function impactBarsLayout(
  baseLayout: CarouselLayout,
  data: Data,
  dimensionLayout: DimensionBarsLayout | undefined,
): ImpactBarsLayout {
  const spaceForBarLabel = dimensionLayout ? dimensionLayout.spaceForBarLabel : 25;
  const marginForBar = dimensionLayout ? dimensionLayout.marginForBar : 4;

  function getMaxScore() {
    if (dimensionLayout) {
      // dimensionLayout.maxX is already ghost-aware when ghost data exists —
      // see `dimensionBarsLayout`.
      return dimensionLayout.maxX;
    }
    if (data.insights) {
      return 3;
    }
    if (data.scores) {
      // Ghost baselines are >= the solid scores, so they must participate in
      // the scale or their tails would overflow the bar range area.
      const ghostMax = data.ghostScores ? maximumImpactScore(data.ghostScores) : 0;
      return Math.max(maximumImpactScore(data.scores), ghostMax, 1);
    }
    // should not really happen
    return 3;
  }

  const maxScore = getMaxScore();

  function hasBars() {
    return data.scores || data.insights;
  }

  function impactBarRangeArea(side: Side, impact: ImpactCategoryId) {
    const da = baseLayout.impactDataArea(side, impact);
    if (!hasBars()) {
      if (side === Side.RIGHT) {
        return toBounds({
          x1: da.x1,
          x2: da.x1,
          y1: da.y1,
          y2: da.y2,
        });
      } else {
        return toBounds({
          x1: da.x2,
          x2: da.x2,
          y1: da.y1,
          y2: da.y2,
        });
      }
    }
    if (side === Side.RIGHT) {
      const x1 = da.x1 + marginForBar;
      const x2 = da.x2 - spaceForBarLabel;
      return toBounds({
        x1,
        x2,
        y1: da.y1,
        y2: da.y2,
      });
    } else {
      const x2 = da.x2 - marginForBar;
      const x1 = da.x1 + spaceForBarLabel;
      return toBounds({
        x1,
        x2,
        y1: da.y1,
        y2: da.y2,
      });
    }
  }

  function impactBarArea(side: Side, impact: ImpactCategoryId, valueChain?: ValueChain) {
    const ra = impactBarRangeArea(side, impact);

    function getXValues() {
      function getScore() {
        const { scores, insights } = data;
        if (scores) {
          return pickScore(scores, impact, valueChain, sideToValence(side));
        }
        if (insights) {
          const insight = insights.find(d => d.impactCategoryId === impact && d.valence === sideToValence(side));
          return insight ? insight.magnitude : 0;
        }
        return undefined;
      }

      function getBaseScore() {
        if (valueChain === undefined) {
          return 0;
        }
        const { scores } = data;
        if (scores) {
          return previousValueChains(valueChain).reduce(
            (acc, vc) => acc + pickScore(scores, impact, vc, sideToValence(side)),
            0,
          );
        }
        return 0;
      }

      const score = getScore();
      const baseScore = getBaseScore();
      if (score === undefined) {
        return { x1: ra.x1, x2: ra.x2 };
      }
      if (side === Side.RIGHT) {
        const x1 = ra.x1 + (ra.width * baseScore) / maxScore;
        const x2 = ra.x1 + (ra.width * (score + baseScore)) / maxScore;
        return { x1, x2 };
      } else {
        const x2 = ra.x2 - (ra.width * baseScore) / maxScore;
        const x1 = ra.x2 - (ra.width * (baseScore + score)) / maxScore;
        return { x1, x2 };
      }
    }

    const barHeight = dimensionLayout ? 6 : 12;
    const y1 = ra.y1 + ra.height / 2 - barHeight / 2;
    const y2 = y1 + barHeight;
    const { x1, x2 } = getXValues();
    return toBounds({ x1, x2, y1, y2 });
  }

  function impactGhostTailArea(side: Side, impact: ImpactCategoryId): Bounds | undefined {
    const { scores, ghostScores } = data;
    if (!scores || !ghostScores) {
      return undefined;
    }
    const ra = impactBarRangeArea(side, impact);
    const barArea = impactBarArea(side, impact);
    const ghostScore = pickScore(ghostScores, impact, undefined, sideToValence(side));
    const ghostWidth = (ra.width * ghostScore) / maxScore;
    // 2px gap between the solid bar and its tail (mirrors the stacked-bar
    // ghost in the multi-series chart); the 1px minimum swallows float noise
    // between the baseline and recomputed scores so unaffected bars never
    // grow phantom tails.
    const gap = barArea.width > 0 ? 2 : 0;
    const tailWidth = ghostWidth - barArea.width - gap;
    if (tailWidth < 1) {
      return undefined;
    }
    const { x1, x2 } =
      side === Side.RIGHT
        ? { x1: barArea.x2 + gap, x2: barArea.x2 + gap + tailWidth }
        : { x1: barArea.x1 - gap - tailWidth, x2: barArea.x1 - gap };
    return toBounds({ x1, x2, y1: barArea.y1, y2: barArea.y2 });
  }

  function impactBarDividerArea(index: number, side: Side, impact: ImpactCategoryId) {
    const ra = impactBarRangeArea(side, impact);
    const score = index + 1;
    const dividerWidth = 2;
    const barHeight = 12;

    function getXValues() {
      if (side === Side.RIGHT) {
        const x1 = ra.x1 + (ra.width * score) / maxScore;
        const x2 = x1 + dividerWidth;
        return { x1, x2 };
      } else {
        const x2 = ra.x2 - (ra.width * score) / maxScore;
        const x1 = x2 - dividerWidth;
        return { x1, x2 };
      }
    }

    const { x1, x2 } = getXValues();
    const y1 = ra.y1 + ra.height / 2 - barHeight / 2;
    const y2 = y1 + barHeight;
    return toBounds({ x1, x2, y1, y2 });
  }

  function impactBarLabelArea(side: Side, impact: ImpactCategoryId) {
    const barArea = impactBarArea(side, impact);
    // The label hangs off the bar's outer edge; a ghost tail pushes it
    // outward so the number is never drawn on top of the tail.
    const da = impactGhostTailArea(side, impact) ?? barArea;

    // when not in context where dimension bars are drawn, we
    // draw impact bars inside, meaning that no space reservation needed
    const usedSpace = dimensionLayout ? spaceForBarLabel : 0;

    function getXValues() {
      if (!hasBars()) {
        return { x1: da.x1, x2: da.x2 };
      }
      if (side === Side.RIGHT) {
        const x1 = da.x2;
        const x2 = da.x2 + usedSpace;
        return {
          x1,
          x2,
        };
      } else {
        const x1 = da.x1 - usedSpace;
        const x2 = da.x1;
        return {
          x1,
          x2,
        };
      }
    }

    const { x1, x2 } = getXValues();
    return toBounds({
      x1: x1,
      x2: x2,
      y1: da.y1,
      y2: da.y2,
    });
  }

  function impactBarAndLabelArea(side: Side, impact: ImpactCategoryId) {
    const barArea = impactBarArea(side, impact);
    const labelArea = impactBarLabelArea(side, impact);
    const x1 = side === Side.RIGHT ? barArea.x1 : labelArea.x1;
    const x2 = side === Side.RIGHT ? labelArea.x2 : barArea.x2;
    const y1 = barArea.y1;
    const y2 = barArea.y2;
    return toBounds({ x1, x2, y1, y2 });
  }

  return {
    impactBarArea,
    impactBarDividerArea,
    impactBarLabelArea,
    impactBarRangeArea,
    impactBarAndLabelArea,
    impactGhostTailArea,
    dimensionLayout,
  };
}

function maximumImpactScore(scores: ImpactScoresByIVV) {
  const impactCodes = Object.keys(scores);
  let max = 0;
  const valences = [Valence.POSITIVE, Valence.NEGATIVE];
  impactCodes.forEach(code => {
    valences.forEach(valence => {
      max = Math.max(max, pickScore(scores, code, undefined, valence));
    });
  });
  return max;
}

function previousValueChains(valueChain: ValueChain): ValueChain[] {
  switch (valueChain) {
    case ValueChain.UPSTREAM:
      return [];
    case ValueChain.INTERNAL:
      return [ValueChain.UPSTREAM];
    case ValueChain.DOWNSTREAM:
      return [ValueChain.UPSTREAM, ValueChain.INTERNAL];
    case ValueChain.ANY:
      // ANY is the aggregate bucket, not one of the stacked U→I→D segments, so
      // it has no preceding chains to offset against. The sole caller never
      // passes it (it guards `undefined` and stacks only the three concrete
      // chains), but the case is required for switch exhaustiveness. No default
      // clause: the exhaustiveness-check rule then forces any future ValueChain
      // member to be handled here explicitly.
      return [];
  }
}
